/**
 * The committed defaults for every field the admin can edit.
 *
 * These live here rather than in the site because two places need them: the
 * site renders them when nothing has been published, and the admin shows them
 * as the current text so an editor is not staring at empty boxes wondering what
 * the website actually says. One definition, so the two cannot disagree.
 *
 * Deliberately free of zod and of any import at all. The site imports this
 * module directly, via its own export path, so building the website does not
 * pull a validation library into the graph for a plain object.
 *
 * Anything NOT editable stays in apps/site/src/config.ts: the business name,
 * the service-area summary, and the service slugs are structural, and changing
 * them from a text box would break URLs and meta descriptions.
 */
export const siteDefaults = {
  /**
   * Used as the homepage H1, the footer line, and the JSON-LD description.
   *
   * It names the region deliberately: an H1 containing the service area is the
   * strongest on-page local-SEO signal available. The cost is that anything
   * else on the homepage stating the region becomes an echo -- which is why the
   * hero eyebrow is gone and the homepage meta description no longer repeats it.
   */
  tagline: "Cleaning you can count on, from Barrie to North Bay",

  phone: "(705) 571-1319",

  /**
   * The public address: shown on the contact page and used by the contact
   * form's mailto fallback.
   *
   * App-generated mail is separate -- SES sends from mail.huntscleaning.ca and
   * notifies alert_email in infra/prod.tfvars, which is this same mailbox.
   */
  email: "richard@huntscleaning.ca",

  /**
   * The Highway 11 corridor, Barrie north to North Bay.
   *
   * Listed town by town rather than as a region because these names are what
   * people actually search ("office cleaning Bracebridge"), and they become
   * areaServed City entries in the structured data Google uses for local
   * results. Add towns as coverage genuinely extends -- claiming a town you
   * cannot service is worse than omitting it.
   */
  serviceAreas: [
    "Barrie",
    "Orillia",
    "Gravenhurst",
    "Bracebridge",
    "Huntsville",
    "Burk's Falls",
    "Sundridge",
    "South River",
    "Powassan",
    "Callander",
    "North Bay",
  ],

  /** TODO: confirm. Shown on the contact page. */
  hours: "Monday–Friday, 7am–6pm. After-hours and weekend service available.",

  /**
   * TODO: STILL UNVERIFIED. These sit in a dark band across the homepage, which
   * is exactly where a facility manager looks to disqualify a contractor, so a
   * claim that turns out to be false does real damage. Confirm each one or
   * delete it -- an empty list renders nothing and breaks no layout.
   *
   * "Police-checked, trained staff" was removed outright: the crews are
   * subcontracted, not employed, so "staff" was inaccurate regardless of
   * whether screening happens. WSIB and liability coverage also work
   * differently with subcontractors -- coverage may be the subcontractor's
   * obligation rather than yours, so confirm what Hunt's itself holds before
   * making these claims in its own name.
   */
  credentials: [
    "Fully insured and bonded",
    "WSIB coverage in good standing",
    "Locally owned and operated",
    "Vetted local cleaners",
  ],

  /** TODO: real founding year. */
  foundedYear: 2024,

  /**
   * The slug is structural -- it is the anchor in /services#commercial and the
   * homepage links point at it -- so it is not editable. The words are.
   */
  services: [
    {
      slug: "commercial",
      title: "Commercial cleaning",
      summary:
        "Offices, clinics, retail, and professional spaces kept presentable for staff and clients, on a schedule that works around your business hours.",
      points: [
        "Nightly, weekly, or custom schedules",
        "Reception, workstations, boardrooms, kitchens",
        "Washroom sanitation and consumable restocking",
        "Floor care: vacuuming, mopping, spot treatment",
      ],
    },
    {
      slug: "industrial",
      title: "Industrial & facility cleaning",
      summary:
        "Warehouses, shops, and production floors, cleaned to a standard that holds up to inspection and keeps your team safe.",
      points: [
        "Warehouse and production floor scrubbing",
        "High-traffic and loading-dock areas",
        "Post-construction and site cleanup",
        "Health-and-safety-conscious crews",
      ],
    },
    {
      slug: "residential",
      title: "Residential cleaning",
      summary:
        "Recurring or one-time cleaning for homes, condos, and rental turnovers, done to the same checklist every visit.",
      points: [
        "Weekly, biweekly, or monthly visits",
        "Deep cleans and seasonal refreshes",
        "Move-in / move-out and rental turnovers",
        "Same written standard every visit",
      ],
    },
  ],
};
